import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Container,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
} from "@mui/material";
import axios from "axios";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SchoolIcon from "@mui/icons-material/School";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { fetchScholarDashboard, ScholarDashboardResponse } from "./dashboardApi";
import { useMasterData } from "../../../hooks/useMasterData";

const GENDER_COLORS: Record<string, string> = {
  male: "#42a5f5",
  female: "#ef5350",
  other: "#ab47bc",
  unknown: "#9e9e9e",
};

const CATEGORY_COLORS = ["#42a5f5", "#66bb6a", "#ffca28", "#ef5350", "#ab47bc"];

interface AcademicSession {
  id: string;
  name: string;
  isActive: boolean;
}

const SCHOLAR_DASHBOARD_FILTERS_KEY = "scholar_register_dashboard_filters";

const ScholarDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { masters, loading: mastersLoading, fetchMasters, fetchSections } =
    useMasterData();

  const [filters, setFilters] = useState<{
    academicSessionId?: string;
    classId?: string;
    sectionId?: string;
  }>(() => {
    try {
      const raw = sessionStorage.getItem(SCHOLAR_DASHBOARD_FILTERS_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return {
        academicSessionId: parsed?.academicSessionId || undefined,
        classId: parsed?.classId || undefined,
        sectionId: parsed?.sectionId || undefined,
      };
    } catch {
      return {};
    }
  });
  const [data, setData] = useState<ScholarDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sections, setSections] = useState<any[]>([]);
  const [sessions, setSessions] = useState<AcademicSession[]>([]);

  const loadSessions = useCallback(async () => {
    try {
      const res = await axios.get<AcademicSession[]>("/system-settings/academic-sessions");
      const allSessions = res.data || [];
      setSessions(allSessions);

      const active = allSessions.find((s) => s.isActive);
      if (active) {
        setFilters((prev) =>
          prev.academicSessionId ? prev : { ...prev, academicSessionId: active.id },
        );
      }
    } catch {
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    fetchMasters();
    loadSessions();
  }, [fetchMasters, loadSessions]);

  useEffect(() => {
    sessionStorage.setItem(SCHOLAR_DASHBOARD_FILTERS_KEY, JSON.stringify(filters || {}));
  }, [filters]);

  useEffect(() => {
    if (filters.classId) {
      fetchSections(filters.classId).then((secs) => setSections(secs || []));
    } else {
      setSections([]);
    }
  }, [filters.classId, fetchSections]);

  const loadDashboard = async (currentFilters: {
    academicSessionId?: string;
    classId?: string;
    sectionId?: string;
  }) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchScholarDashboard(currentFilters);
      setData(resp);
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        "Failed to load Scholar Register dashboard. Please try again.";
      setError(typeof msg === "string" ? msg : "Failed to load dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.academicSessionId, filters.classId, filters.sectionId]);

  const genderChartData = useMemo(() => {
    if (!data) return [];
    const entries: { name: string; value: number; key: string }[] = [];
    const stats = data.genderStats || {};

    const pushIfPositive = (key: string, label: string) => {
      const value = stats[key] || 0;
      if (value > 0) {
        entries.push({ name: label, value, key });
      }
    };

    pushIfPositive("male", "Male");
    pushIfPositive("female", "Female");
    pushIfPositive("other", "Other");
    if (!entries.length && Object.keys(stats).length > 0) {
      for (const [k, v] of Object.entries(stats)) {
        entries.push({ name: k, value: v, key: k });
      }
    }

    return entries;
  }, [data]);

  const handleFilterChange = (key: "academicSessionId" | "classId" | "sectionId", value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
      ...(key === "classId"
        ? { sectionId: undefined }
        : {}),
    }));
  };

  const handleResetFilters = () => {
    const active = sessions.find((s) => s.isActive);
    const next = {
      academicSessionId: active?.id || undefined,
      classId: undefined,
      sectionId: undefined,
    };
    setFilters(next);
    sessionStorage.setItem(SCHOLAR_DASHBOARD_FILTERS_KEY, JSON.stringify(next));
  };

  const handleCardClick = (type: string) => {
    switch (type) {
      case "total":
        navigate("/scholar-register/students");
        break;
      case "rte":
        navigate("/scholar-register/rte");
        break;
      case "bpl":
        navigate("/scholar-register/bpl");
        break;
      case "active":
        navigate("/scholar-register/status");
        break;
      case "inactive":
        navigate("/scholar-register/status");
        break;
      default:
        break;
    }
  };

  const handleRowClick = (id: string) => {
    navigate(`/scholar-register/students/${id}`);
  };

  const renderTopCards = () => {
    if (loading && !data) {
      return (
        <Grid container spacing={2}>
          {Array.from({ length: 5 }).map((_, idx) => (
            <Grid item xs={12} sm={6} md={2.4} key={idx}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      );
    }

    if (!data) return null;

    const cards = [
      {
        key: "total",
        label: "Total Students",
        value: data.totalStudents,
        icon: <PeopleIcon color="primary" />,
      },
      {
        key: "active",
        label: "Active Students",
        value: data.activeStudents,
        icon: <CheckCircleIcon color="success" />,
      },
      {
        key: "inactive",
        label: "Inactive Students",
        value: data.inactiveStudents,
        icon: <HighlightOffIcon color="error" />,
      },
      {
        key: "rte",
        label: "RTE Students",
        value: data.rteStudents,
        icon: <SchoolIcon color="secondary" />,
      },
      {
        key: "bpl",
        label: "BPL Students",
        value: data.bplStudents,
        icon: <AccountBalanceWalletIcon color="warning" />,
      },
    ];

    return (
      <Grid container spacing={2}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={2.4} key={card.key}>
            <Card>
              <CardActionArea onClick={() => handleCardClick(card.key)}>
                <CardContent>
                  <Box
                    sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                  >
                    {card.icon}
                    <Typography variant="h5">{card.value}</Typography>
                  </Box>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
                    {card.label}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Student Analytics Dashboard
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Real-time insights for Scholar Register
          </Typography>
        </Box>
        <IconButton onClick={() => loadDashboard(filters)} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 3, borderRadius: 2, display: "flex", gap: 2, flexWrap: "wrap" }}
      >
        <Box sx={{ minWidth: 200 }}>
          <Typography variant="caption" color="textSecondary">
            Academic Session
          </Typography>
          <Select
            fullWidth
            size="small"
            value={filters.academicSessionId || ""}
            onChange={(e) =>
              handleFilterChange("academicSessionId", e.target.value as string)
            }
            displayEmpty
          >
            <MenuItem value="">
              <em>All Sessions</em>
            </MenuItem>
            {sessions.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
                {s.isActive ? " (Current)" : ""}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <Typography variant="caption" color="textSecondary">
            Class
          </Typography>
          <Select
            fullWidth
            size="small"
            value={filters.classId || ""}
            onChange={(e) => handleFilterChange("classId", e.target.value as string)}
            displayEmpty
          >
            <MenuItem value="">
              <em>All Classes</em>
            </MenuItem>
            {masters.classes.map((c: any) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <Typography variant="caption" color="textSecondary">
            Section
          </Typography>
          <Select
            fullWidth
            size="small"
            value={filters.sectionId || ""}
            onChange={(e) => handleFilterChange("sectionId", e.target.value as string)}
            displayEmpty
          >
            <MenuItem value="">
              <em>All Sections</em>
            </MenuItem>
            {sections
              .filter((s: any) => !filters.classId || s.classId === filters.classId)
              .map((s: any) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
          </Select>
        </Box>
        <Box sx={{ display: "flex", alignItems: "end", minWidth: 140 }}>
          <Button variant="outlined" fullWidth onClick={handleResetFilters}>
            Reset Filters
          </Button>
        </Box>
      </Paper>

      <Box sx={{ mb: 3 }}>{renderTopCards()}</Box>

      {error && (
        <Box sx={{ mb: 3 }}>
          <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 2, borderColor: "error.main" }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography color="error">{error}</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => loadDashboard(filters)}
              >
                Retry
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: 320 }}>
            <Typography variant="subtitle1" gutterBottom>
              Gender Distribution
            </Typography>
            <Box sx={{ height: 260 }}>
              {loading && !data ? (
                <Skeleton variant="rectangular" width="100%" height="100%" />
              ) : genderChartData.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No data available.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      dataKey="value"
                      data={genderChartData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label
                    >
                      {genderChartData.map((entry, index) => (
                        <Cell
                          key={entry.key}
                          fill={
                            GENDER_COLORS[entry.key] ||
                            GENDER_COLORS[entry.key.toLowerCase()] ||
                            "#90a4ae"
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: 320 }}>
            <Typography variant="subtitle1" gutterBottom>
              Class Wise Strength
            </Typography>
            <Box sx={{ height: 260 }}>
              {loading && !data ? (
                <Skeleton variant="rectangular" width="100%" height="100%" />
              ) : !data || data.classStats.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No data available.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.classStats}>
                    <XAxis dataKey="class" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="students" fill="#42a5f5" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: 320 }}>
            <Typography variant="subtitle1" gutterBottom>
              Section Wise Strength
            </Typography>
            <Box sx={{ height: 260 }}>
              {loading && !data ? (
                <Skeleton variant="rectangular" width="100%" height="100%" />
              ) : !data || data.sectionStats.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No data available.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.sectionStats}>
                    <XAxis dataKey="section" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="students" fill="#66bb6a" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: 320 }}>
            <Typography variant="subtitle1" gutterBottom>
              Category Distribution
            </Typography>
            <Box sx={{ height: 260 }}>
              {loading && !data ? (
                <Skeleton variant="rectangular" width="100%" height="100%" />
              ) : !data || data.categoryStats.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No data available.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      dataKey="students"
                      data={data.categoryStats}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      label={(entry: any) => entry.category}
                    >
                      {data.categoryStats.map((entry, index) => (
                        <Cell
                          key={entry.categoryId || index}
                          fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Recent Admissions
            </Typography>
            {loading && !data ? (
              <Skeleton variant="rectangular" width="100%" height={200} />
            ) : !data || data.recentAdmissions.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No recent admissions found.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Admission No</TableCell>
                      <TableCell>Student Name</TableCell>
                      <TableCell>Class</TableCell>
                      <TableCell>Section</TableCell>
                      <TableCell>Admission Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.recentAdmissions.map((s) => (
                      <TableRow
                        key={s.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleRowClick(s.id)}
                      >
                        <TableCell>{s.admissionNumber}</TableCell>
                        <TableCell>{s.name}</TableCell>
                        <TableCell>{s.class}</TableCell>
                        <TableCell>{s.section}</TableCell>
                        <TableCell>
                          {s.admissionDate
                            ? String(s.admissionDate).substring(0, 10)
                            : ""}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ScholarDashboard;
